/**
 * Shopify CDN image resizing utility.
 * Appends width parameter to Shopify-hosted image URLs for on-the-fly resizing.
 * This can reduce image payload by 60-80% by requesting only the size needed.
 */

const SHOPIFY_CDN_PATTERN = /cdn\.shopify\.com/;

/**
 * Returns a resized Shopify CDN image URL.
 * If the URL is not from Shopify CDN, returns it unchanged.
 *
 * @param url - Original image URL
 * @param width - Desired width in pixels
 * @returns Resized URL string
 */
export function shopifyImageUrl(url: string, width: number): string {
  if (!url || !SHOPIFY_CDN_PATTERN.test(url)) return url;

  // Shopify CDN supports ?width= param
  try {
    const u = new URL(url);
    u.searchParams.set("width", String(Math.round(width)));
    // Request webp format for modern browsers
    u.searchParams.set("format", "webp");
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Generate a srcSet string for Shopify CDN images at 1x and 2x.
 */
export function shopifySrcSet(url: string, width: number): string | undefined {
  if (!url || !SHOPIFY_CDN_PATTERN.test(url)) return undefined;
  return `${shopifyImageUrl(url, width)} 1x, ${shopifyImageUrl(url, width * 2)} 2x`;
}
